import { useEffect } from 'react'

/**
 * The nav's behaviour, in React instead of Webflow's engine.
 *
 * Webflow shipped this as `webflow.js` + jQuery — 331 KB to open three
 * dropdowns and a mobile menu. It also had no Escape handling, no roving
 * focus, and an `aria-expanded` that did not survive keyboard use, which is
 * what `README.md` flagged and what this fixes.
 *
 * It wires the ported markup rather than replacing it. That markup is
 * pixel-exact and five phases of `dom-diff` have held it to the capture; a
 * different DOM would have thrown that away to gain nothing visible. The
 * contract is small and entirely in classes the stylesheet already defines:
 *
 *   .w-dropdown-list.w--open  { display: block }   opens a panel
 *   .w-nav-button.w--open                          the burger's active state
 *   [data-nav-menu-open]                           the mobile sheet, styled in chrome.css
 *
 * Runs against whatever is in the document, so it does not care that the
 * markup is generated.
 */
export function useChromeNav(route: string) {
  useEffect(() => {
    const header = document.getElementById('header')
    if (!header) return

    const cleanups: Array<() => void> = []
    const on = <K extends keyof HTMLElementEventMap>(
      el: EventTarget,
      type: K | string,
      fn: (e: Event) => void,
      opts?: AddEventListenerOptions,
    ) => {
      el.addEventListener(type, fn as EventListener, opts)
      cleanups.push(() => el.removeEventListener(type, fn as EventListener, opts))
    }

    /* ── Desktop dropdowns ──────────────────────────────────────────── */
    const dropdowns = [...header.querySelectorAll<HTMLElement>('.w-dropdown')]

    /*
     * Which panels a click opened, as opposed to a hover.
     *
     * Webflow toggles on click regardless, so hovering a panel open and then
     * clicking it shut again is the capture's behaviour — and a bad surprise:
     * the pointer is already over the item the user is reaching for. A click
     * opens; it only closes what a click opened.
     */
    const clickOpened = new WeakSet<HTMLElement>()

    const setOpen = (dd: HTMLElement, open: boolean) => {
      const toggle = dd.querySelector<HTMLElement>('.w-dropdown-toggle')
      const list = dd.querySelector<HTMLElement>('.w-dropdown-list')
      if (!toggle || !list) return
      toggle.classList.toggle('w--open', open)
      list.classList.toggle('w--open', open)
      toggle.setAttribute('aria-expanded', String(open))
    }

    const closeAll = (except?: HTMLElement) => {
      for (const dd of dropdowns) {
        if (dd === except) continue
        clickOpened.delete(dd)
        setOpen(dd, false)
      }
    }

    /*
     * Hover is how the capture opens these (`data-hover="true"`), but only
     * where hovering is how the device actually works — on a touch screen a
     * hover-open panel opens on the first tap and swallows it.
     */
    const canHover = window.matchMedia('(hover: hover) and (min-width: 992px)')

    for (const dd of dropdowns) {
      const toggle = dd.querySelector<HTMLElement>('.w-dropdown-toggle')
      const list = dd.querySelector<HTMLElement>('.w-dropdown-list')
      if (!toggle || !list) continue

      on(dd, 'mouseenter', () => {
        if (canHover.matches) {
          closeAll(dd)
          setOpen(dd, true)
        }
      })
      on(dd, 'mouseleave', () => {
        // A click-opened panel stays until it is dismissed deliberately.
        if (canHover.matches && !clickOpened.has(dd)) setOpen(dd, false)
      })

      on(toggle, 'click', () => {
        const shut = clickOpened.has(dd) && toggle.classList.contains('w--open')
        closeAll(dd)
        if (shut) {
          clickOpened.delete(dd)
          setOpen(dd, false)
        } else {
          clickOpened.add(dd)
          setOpen(dd, true)
        }
      })

      on(toggle, 'keydown', (e) => {
        const ev = e as KeyboardEvent
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault()
          const open = toggle.classList.contains('w--open')
          closeAll(dd)
          if (open) clickOpened.delete(dd)
          else clickOpened.add(dd)
          setOpen(dd, !open)
          return
        }
        // Not in the original: open and step into the panel.
        if (ev.key === 'ArrowDown') {
          ev.preventDefault()
          closeAll(dd)
          setOpen(dd, true)
          list.querySelector<HTMLElement>('a[href], [tabindex="0"]')?.focus()
        }
      })

      // Escape closes and hands focus back, which the capture never did.
      on(dd, 'keydown', (e) => {
        const ev = e as KeyboardEvent
        if (ev.key !== 'Escape') return
        if (!toggle.classList.contains('w--open')) return
        ev.stopPropagation()
        setOpen(dd, false)
        toggle.focus()
      })

      // Tabbing out of a panel should close it.
      on(dd, 'focusout', (e) => {
        const next = (e as FocusEvent).relatedTarget
        if (next instanceof Node && dd.contains(next)) return
        setOpen(dd, false)
      })
    }

    /* ── Mobile menu ────────────────────────────────────────────────── */
    const button = header.querySelector<HTMLElement>('.w-nav-button')
    const menu = header.querySelector<HTMLElement>('.w-nav-menu')

    const setMenu = (open: boolean) => {
      if (!button || !menu) return
      button.classList.toggle('w--open', open)
      button.setAttribute('aria-expanded', String(open))
      if (open) menu.setAttribute('data-nav-menu-open', '')
      else menu.removeAttribute('data-nav-menu-open')
      if (!open) closeAll()
    }

    if (button && menu) {
      on(button, 'click', () => setMenu(!button.classList.contains('w--open')))
      on(button, 'keydown', (e) => {
        const ev = e as KeyboardEvent
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault()
          setMenu(!button.classList.contains('w--open'))
        }
      })
      // Following a link should not leave the sheet open behind the new page.
      on(menu, 'click', (e) => {
        if ((e.target as Element | null)?.closest('a')) setMenu(false)
      })
    }

    /* ── Global dismissals ──────────────────────────────────────────── */
    on(document, 'click', (e) => {
      if (header.contains(e.target as Node)) return
      closeAll()
      setMenu(false)
    })
    on(document, 'keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Escape') {
        closeAll()
        setMenu(false)
      }
    })

    /*
     * Bound per route, not once: /studio swaps in its own header, so coming
     * back mounts a fresh one that a single first-load binding never reached
     * (the dropdowns stopped opening). Rebinding on each route also starts
     * every page with the panels shut.
     */
    closeAll()
    setMenu(false)

    return () => {
      for (const off of cleanups) off()
    }
  }, [route])
}
